import React, { useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import './_control-form.scss';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import OptionsButton from '../../../../global/options-button/options-button';
import { MeetingProgress } from '../../../../../api/control.dao';
import { AttendeeRole } from '../../../../../api/attendees.dao';
import { MeetingStatus } from '../../../../../api/meetings.dao';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { AddNewEntityButton } from '../../../../global/add-new-entity/add-new-entity-button';
import { ControlFormBody } from './_control-form-body';
import { ControlFormVM } from './_control-form.viewmodel';
import { DONE_TEXT, MORE_BUTTON_TEXT } from '../../../../../constants/common-labels.constants';

interface IProps {
    vm: ControlFormVM;
    intl: IntlShape;
    isExpanded: boolean;
    setMeetingStartDate: () => void;
}

const ControlFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl, isExpanded, setMeetingStartDate } = props;

    const formOptions = [
        hasPermission(Permission.UPDATE_MEETING) && {
            label: intl.formatMessage({
                id: 'oe.meeting.editMeeting',
                defaultMessage: 'Edit Meeting',
                description: 'Label text'
            }),
            action: vm.editMeeting,
        },
        hasPermission(Permission.CONNECTIONS_MANAGEMENT) && {
            label: intl.formatMessage({
                id: 'oe.meeting.editMeetingConnections',
                defaultMessage: 'Edit Meeting Connections',
                description: 'Label text'
            }),
            action: vm.editMeetingConnections,
            disabled: !vm.isUniqueJoiningLinksDeletionTriggerEnabled,
        },
        {
            label: intl.formatMessage({
                id: 'oe.meeting.cancelMeeting',
                defaultMessage: 'Cancel Meeting',
                description: 'Label text'
            }),
            action: vm.cancelMeeting,
            disabled: vm.status === MeetingStatus.Cancelled || vm.progress === MeetingProgress.ENDED,
            danger: true
        }
    ].filter(Boolean);

    useEffect(() => {
        return () => {
            vm.onUnmount();
        };
    }, []);

    if (vm.loading) {
        return <CommonPopupBodyFooterLayout body={<div className='oe-form meeting-form'>
            <FormattedMessage
                id='oe.meeting.loadingMeetingData'
                defaultMessage='loading meeting data...'
                description='Preloader label text'
            />
        </div>} />;
    }

    return (
        <CommonPopupBodyFooterLayout
            body={<ControlFormBody
                vm={vm}
                isExpanded={isExpanded}
                setMeetingStartDate={setMeetingStartDate}
                ActionButton={({ role }: { role: AttendeeRole }) =>
                    <AddNewEntityButton
                        permission={Permission.CREATE_ATTENDEE}
                        onClick={() => vm.createAttendeeFromForm(role)}
                        label={
                            <FormattedMessage
                                description='Label text'
                                defaultMessage='{prefix}New Attendee'
                                id='oe.attendee.new'
                                values={{
                                    prefix: '+ '
                                }}
                            />}
                    />
                }
            />}
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        modifier='fat'
                        text={<FormattedMessage {...DONE_TEXT} />}
                    />
                    {!!formOptions.length && <OptionsButton
                        className='options-button--right-margin-auto'
                        modifier={['fat', 'options']}
                        label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                        options={formOptions}
                        position='top'
                        align='start'
                    />}
                </div>
            }
        />
    );
}));

const ControlFormContainer = mobxReact.observer((props: IProps) => {
    return <ControlFormPresentation {...props} />;
});

export { ControlFormPresentation, ControlFormContainer as ControlForm };
